import { useQuery } from '@tanstack/react-query'
import {
  patientService,
  staffService,
  documentService,
  financialService,
  chatService,
} from '@/services/api'
import { queryKeys } from '@/config/queryKeys'
import type { GetPatientsParams } from '@/services/api/patients/patient.static'

export const useRecentPatients = (params?: GetPatientsParams) => {
  const queryParams: GetPatientsParams = {
    page: 1,
    limit: 5,
    status: 'ACTIVE',
    ...params,
  }

  return useQuery({
    queryKey: queryKeys.patients.list({ ...queryParams }),
    queryFn: () => patientService.getPatients(queryParams),
  })
}

export const useDashboardStats = () => {
  const patientsQuery = useQuery({
    queryKey: queryKeys.patients.list({ page: 1, limit: 1 }),
    queryFn: () => patientService.getPatients({ page: 1, limit: 1 }),
  })

  const activePatientsQuery = useQuery({
    queryKey: queryKeys.patients.list({ page: 1, limit: 1, status: 'ACTIVE' }),
    queryFn: () => patientService.getPatients({ page: 1, limit: 1, status: 'ACTIVE' }),
  })

  const staffQuery = useQuery({
    queryKey: queryKeys.staff.list({ page: 1, limit: 1, status: 'ACTIVE' }),
    queryFn: () => staffService.getStaff({ page: 1, limit: 1, status: 'ACTIVE' }),
  })

  const documentsQuery = useQuery({
    queryKey: queryKeys.documents.list({ page: 1, limit: 1 }),
    queryFn: () => documentService.getDocuments({ page: 1, limit: 1 }),
  })

  const financialQuery = useQuery({
    queryKey: queryKeys.financials.summary('current'),
    queryFn: () => financialService.getSummary(),
  })

  const isLoading =
    patientsQuery.isLoading ||
    activePatientsQuery.isLoading ||
    staffQuery.isLoading ||
    documentsQuery.isLoading ||
    financialQuery.isLoading

  const isError =
    patientsQuery.isError ||
    activePatientsQuery.isError ||
    staffQuery.isError ||
    documentsQuery.isError ||
    financialQuery.isError

  return {
    totalPatients: patientsQuery.data?.total ?? 0,
    activePatients: activePatientsQuery.data?.total ?? 0,
    totalStaff: staffQuery.data?.total ?? 0,
    totalDocuments: documentsQuery.data?.total ?? 0,
    financialSummary: financialQuery.data ?? null,
    isLoading,
    isError,
  }
}

export const useChatStatus = () => {
  return useQuery({
    queryKey: queryKeys.chat.status(),
    queryFn: () => chatService.getStatus(),
    refetchInterval: 60000,
  })
}
